import InstagramIcon from "@/assets/colorable/instagram.svg?react";
import TiktokIcon from "@/assets/colorable/tiktok.svg?react";
import {getPackageIcon} from "@/hooks/usePackages";
import {usePriceFormat} from "@/hooks/usePriceFormat";
import clsxm from "@/lib/clsxm";
import type {TPackageCategory, TPackageName, TPackagePaymentPeriodType} from "@/store/types";
import {type FC} from "react";
import {useTranslation} from "react-i18next";

const MiniPlanInfoCard: FC<{
  chipsClassName?: string;
  className?: string;
  highlightTiktok?: boolean;
  name?: TPackageName | null;
  packageCategory?: TPackageCategory | null;
  packageType?: TPackagePaymentPeriodType | null;
  priceDifference?: number;
  pricePerMonth?: number | null;
  pricePerYear?: number | null;
}> = ({
  chipsClassName,
  className,
  highlightTiktok,
  name,
  packageCategory,
  packageType,
  priceDifference,
  pricePerMonth,
  pricePerYear,
}) => {
  const {t} = useTranslation("common");
  const formatPrice = usePriceFormat();
  const isYearly = packageType === "YEARLY";
  const showInstagram = packageCategory === "INSTAGRAM" || packageCategory === "BOTH";
  const showTiktok = packageCategory === "TIKTOK" || packageCategory === "BOTH";
  const hasDiff = priceDifference !== undefined && priceDifference > 0;

  return (
    <div
      className={clsxm("bg-neutral-grey-lightest flex items-center justify-between gap-2 rounded-xl p-4", className)}>
      <div className="flex flex-col items-start gap-2">
        <div className="flex items-center gap-2">
          {name && getPackageIcon({packageType: name, className: "size-4", defaultIcon: true})}
          <span className="text-neutral-black text-sm leading-5 font-semibold lowercase first-letter:uppercase sm:text-xl">
            {name}
          </span>
        </div>
        <div className={clsxm("flex items-center gap-2", chipsClassName)}>
          {showInstagram && (
            <>
              <div className="text-neutral-grey-dark flex items-center gap-1">
                <InstagramIcon className="size-2.5" />
                <span className="text-2xs leading-2.5 font-semibold uppercase">{t("instagram")}</span>
              </div>
              <div className="bg-neutral-grey-light size-1 rounded-full" />
            </>
          )}
          {showTiktok && (
            <>
              <div
                className={clsxm(
                  "flex items-center gap-1",
                  highlightTiktok ? "text-success-herbal" : "text-neutral-grey-dark",
                )}>
                <TiktokIcon className="size-2.5" />
                <span className="text-2xs leading-2.5 font-semibold uppercase">{t("tiktok")}</span>
              </div>
              <div className="bg-neutral-grey-light size-1 rounded-full" />
            </>
          )}
          <span className="text-2xs text-neutral-grey-dark leading-2.5 font-semibold uppercase">
            {t(packageType === "MONTHLY" ? "monthly" : "yearly")}
          </span>
        </div>
      </div>
      <div className="flex min-h-12 flex-col items-end justify-center gap-2">
        <div className="flex items-center justify-end gap-2">
          <span className="text-neutral-black text-sm leading-5 font-semibold sm:text-xl">
            {formatPrice(isYearly ? pricePerYear : pricePerMonth)}*
          </span>
          <span className="text-neutral-grey-dark text-sm leading-3.5 font-normal capitalize">
            /{t(isYearly ? "yearly" : "monthly")}
          </span>
        </div>
        {hasDiff ? (
          <div className="text-neutral-grey-dark flex items-center justify-end gap-1">
            <span className="text-sm font-semibold">+{formatPrice(priceDifference)}*</span>
            <span className="text-2xs leading-2.5 font-normal capitalize">/{t(isYearly ? "yearly" : "monthly")}</span>
          </div>
        ) : (
          isYearly && (
            <div className="text-neutral-grey-dark flex items-center justify-end gap-1">
              <span className="text-sm font-semibold">{formatPrice((pricePerYear ?? 0) / 12)}*</span>
              <span className="text-2xs leading-2.5 font-normal capitalize">/{t("monthly")}</span>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default MiniPlanInfoCard;
